import React, { useEffect, useState } from "react";
import CreateRoleSection from "../CreateRoleSection/CreateRoleSection";
import TagInput from "./TagInput";
import validateOneLiner from "../../../../components/codiOS/utils/validateOneLiner";
import validateProjectDescription from "../../../../components/codiOS/utils/validateProjectDescription";
import { validateWithCodi } from "../../../../components/codiOS/utils/validateWithCodi";

const categories = [
  { name: "Tech", icon: "💻" },
  { name: "Art", icon: "🎨" },
  { name: "Music", icon: "🎵" },
  { name: "Science", icon: "🔬" },
  { name: "Content", icon: "📸" },
  { name: "Fashion", icon: "👗" },
  { name: "Gaming", icon: "🎮" },
  { name: "Food", icon: "🍽️" },
  { name: "Craft", icon: "🏠" },
];

const ProjectInfoForm = ({ formData, setFormData, errors, showErrors, addRole }) => {
  const [title, setTitle] = useState(formData.title || "");
  const [oneLiner, setOneLiner] = useState(formData.oneLiner || "");
  const [description, setDescription] = useState(formData.description || "");
  const [category, setCategory] = useState(formData.category || "");
  const [tags, setTags] = useState(formData.tags || []);
  const [localErrors, setLocalErrors] = useState({});

  useEffect(() => {
    setFormData({ title, oneLiner, description, category, tags });
  }, [title, oneLiner, description, category, tags, setFormData]);

  const handleTitleChange = async (value) => {
    setTitle(value);
    let err = "";

    if (!value.trim()) {
      err = "Please enter a project title.";
    } else {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/projects/check-title/${encodeURIComponent(value.toLowerCase())}`
        );
        const data = await res.json();
        if (data.exists) {
          err = "That project title already exists. Please choose a different name.";
        }
      } catch {
        err = "Error checking title. Try again.";
      }
    }

    setLocalErrors((prev) => ({ ...prev, title: err }));
  };

  const handleOneLinerChange = async (value) => {
    setOneLiner(value);
    let err = "";
    const lengthCheck = validateOneLiner(value);
    if (!lengthCheck.valid) {
      err = lengthCheck.reason === "too short" ? "One-liner is too short." : "One-liner is too long.";
    } else {
      const meaningCheck = await validateWithCodi(value);
      if (!meaningCheck.valid) err = meaningCheck.reason || "One-liner lacks clarity.";
    }
    setLocalErrors((prev) => ({ ...prev, oneLiner: err }));
  };

  const handleDescriptionChange = async (value) => {
    setDescription(value);
    let err = "";
    const lengthCheck = validateProjectDescription(value);
    if (!lengthCheck.valid) {
      err = lengthCheck.reason === "too short" ? "Project description is too short." : "Project description is too long.";
    } else {
      const meaningCheck = await validateWithCodi(value);
      if (!meaningCheck.valid) err = meaningCheck.reason || "Description lacks clarity.";
    }
    setLocalErrors((prev) => ({ ...prev, description: err }));
  };

  return (
    <div className="w-full p-6 md:p-8 rounded-2xl bg-white shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900">Create Your Project</h3>
        <div className="w-full sm:w-auto">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 text-sm rounded-xl border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.name} value={cat.name}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          {showErrors && errors.category && (
            <p className="text-sm text-red-600 mt-1">{errors.category}</p>
          )}
        </div>
      </div>

      <form className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-1">
            <input
              type="text"
              placeholder="Project Title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(showErrors && errors.title) || localErrors.title ? (
              <p className="text-sm text-red-600">{errors.title || localErrors.title}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <input
              type="text"
              placeholder="One Liner"
              value={oneLiner}
              onChange={(e) => handleOneLinerChange(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(showErrors && errors.oneLiner) || localErrors.oneLiner ? (
              <p className="text-sm text-red-600">{errors.oneLiner || localErrors.oneLiner}</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <textarea
            placeholder="Describe your project in detail..."
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            rows={6}
            className="w-full px-4 py-3 text-sm rounded-2xl border border-gray-300 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {(showErrors && errors.description) || localErrors.description ? (
            <p className="text-sm text-red-600">{errors.description || localErrors.description}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <TagInput selectedCategory={category} onTagsChange={setTags} />
          {showErrors && errors.tags && (
            <p className="text-sm text-red-600">{errors.tags}</p>
          )}
        </div>

        <div className="pt-4">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Add a Role</h3>
          <CreateRoleSection onAddRole={addRole} />
        </div>
      </form>
    </div>
  );
};

export default ProjectInfoForm;
